import { DataHoraFormattedPipe } from '@/shared/pipes/data-hora-formatted.pipe';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BuscaGlobalService } from '@services/busca-global.service';
import { Entrada } from '@shared/interfaces/entrada';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { RespostaPaginada } from '@interfaces/respostaPaginada';

@Component({
  selector: 'app-busca-global',
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TableModule,
    TagModule,
    TooltipModule,
    DataHoraFormattedPipe,
  ],
  templateUrl: './busca-global.component.html',
  styleUrl: './busca-global.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuscaGlobalComponent {
  private readonly buscaGlobalService = inject(BuscaGlobalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  protected readonly termoBusca = new FormControl('', { nonNullable: true });
  protected readonly valorBusca = signal('');
  protected readonly carregando = signal(false);
  protected readonly entradas = signal<Entrada[]>([]);
  protected readonly totalRegistros = signal(0);
  protected readonly primeiraLinha = signal(0);
  protected readonly tamanhoPagina = 10;
  private readonly termoConsultaAtual = signal('');
  protected readonly exibirDialog = signal(false);
  protected readonly mensagem = signal('');
  protected readonly modalBreakpoints: Record<string, string> = {
    '960px': '90vw',
    '640px': '96vw',
  };

  protected readonly possuiMensagem = computed(() => this.mensagem().length > 0);
  protected readonly possuiTermoBusca = computed(() => this.valorBusca().trim().length > 0);
  protected readonly exibirDicaPesquisa = computed(() => this.possuiTermoBusca() && !this.possuiMensagem());
  protected readonly quantidadeResultados = computed(() => this.totalRegistros());

  protected pesquisar(event: Event): void {
    event.preventDefault();

    const termo = this.termoBusca.value.trim();
    this.valorBusca.set(termo);

    if (termo.length === 0) {
      this.mensagem.set('Digite o número da entrada ou o nome do paciente.');
      this.entradas.set([]);
      this.totalRegistros.set(0);
      this.primeiraLinha.set(0);
      this.exibirDialog.set(false);
      return;
    }

    this.termoConsultaAtual.set(termo);
    this.mensagem.set('');
    this.entradas.set([]);
    this.totalRegistros.set(0);
    this.primeiraLinha.set(0);
    this.exibirDialog.set(false);

    this.executarBuscaEntradas(0, 'pesquisa');
  }

  protected aoAlterarPaginaBusca(event: TableLazyLoadEvent): void {
    const primeiraLinhaEvento = event.first ?? 0;
    const linhas = event.rows ?? this.tamanhoPagina;

    if (this.termoConsultaAtual().length === 0) {
      return;
    }

    if (primeiraLinhaEvento === this.primeiraLinha()) {
      return;
    }

    const pagina = Math.floor(primeiraLinhaEvento / linhas);
    this.executarBuscaEntradas(pagina, 'paginacao');
  }

  private executarBuscaEntradas(
    pagina: number,
    origem: 'pesquisa' | 'paginacao',
  ): void {
    const termo = this.termoConsultaAtual();
    if (termo.length === 0) {
      return;
    }

    this.carregando.set(true);

    this.buscaGlobalService
      .buscarEntradas(termo, pagina, this.tamanhoPagina)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.carregando.set(false)),
      )
      .subscribe({
        next: (response) => this.tratarResultadoPaginado(response, origem),
        error: () => this.mensagem.set('Não foi possível buscar entradas agora. Tente novamente.'),
      });
  }

  private tratarResultadoPaginado(resposta: RespostaPaginada<Entrada>, origem: 'pesquisa' | 'paginacao'): void {
    const entradas = resposta.content;
    this.entradas.set(entradas);
    this.totalRegistros.set(resposta.totalElements);
    this.primeiraLinha.set(resposta.pageable.offset);

    if (origem === 'paginacao') {
      return;
    }

    if (resposta.totalElements === 0) {
      this.mensagem.set('Nenhuma entrada encontrada para a busca informada.');
      return;
    }

    if (resposta.totalElements === 1 && entradas.length > 0) {
      this.abrirProntuario(entradas[0].id);
      return;
    }

    this.limparBusca();
    this.exibirDialog.set(true);
  }

  protected onTermoBuscaAlterado(event: Event): void {
    const alvo = event.target;
    if (!(alvo instanceof HTMLInputElement)) return;

    this.valorBusca.set(alvo.value);

    if (alvo.value.trim().length === 0) {
      this.mensagem.set('');
    }
  }

  protected abrirProntuario(entradaId: number): void {
    localStorage.setItem('entradaId', String(entradaId));
    this.exibirDialog.set(false);
    void this.router.navigate(['prontuario', entradaId]);
  }

  protected fecharDialog(): void {
    this.exibirDialog.set(false);
  }

  private limparBusca(): void {
    this.termoBusca.setValue('');
    this.valorBusca.set('');
    this.mensagem.set('');
  }
}
